import React, { useState, useRef, useEffect } from "react";
import {
  Routes,
  Route,
  Link,
  NavLink,
  Navigate,
  useParams,
  useMatch,
  useLocation
} from "react-router-dom";
import { FeaturedHelpTopics } from "./featured-help";
import { Article } from "./article";
import { ArticleNotFound } from "./article-not-found";
import { MaskImage, TrackEvent } from "./utils";
import { Helmet } from "react-helmet-async";

// Custom hook for parsing resource centre URL segments
const useResourceCentreParams = () => {
  const { pathname } = useLocation();
  const match = useMatch('/resource-centre/*');
  
  if (!match) {
    return { activeCategory: null, activeArticle: null };
  }
  
  // Remove the base path and split remaining segments
  const pathSegments = pathname.replace(match.pathnameBase, '').split('/').filter(Boolean);
  
  return {
    activeCategory: pathSegments[0] || null,
    activeArticle: pathSegments[1] || null
  };
};

const SubNavButton = (props) => {
  const [isActive, setActive] = useState(false);
  const [isClicked, setClicked] = useState(false);
  const ref = useRef(null);

  // if current category is active, set active state and store active accordion item
  useEffect(() => {
    if (props.isCurrent) {
      setActive(true)
      if (props.isMain) props.setActiveAccordion(props.category);
    }
  }, [props.isCurrent]);

  // store active accordion item on accordion open (main level only)
  useEffect(() => {

    let timeoutId = null;

    if (isActive) {
      if (props.isMain) {
        props.setActiveAccordion(props.category);
      }

      // scroll into view on accordion open (mobile only)
      if (isClicked && AG.utilities.isMobile()) {
        timeoutId = setTimeout(() => {
          ref.current?.scrollIntoView({ behavior: 'smooth', block: "nearest" });
        }, 220);
      }

    }

    return () => {
      clearTimeout(timeoutId)
    }

  }, [isActive]);

  const onOpenCloseAccordion = () => {
    setActive(!isActive);
    setClicked(true);
    props.isMain ?
      TrackEvent('accordion', { 'title': props.text }) :
      props.parentText && TrackEvent('accordion', { 'parent': props.parentText, 'title': props.text })
  }

  const handleClick = () => {
    onOpenCloseAccordion();
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      onOpenCloseAccordion();
    }
  }

  // if current category item is not equal to opened accordion item, set false active state (main level only)
  useEffect(() => {
    if (props.isMain) {
      if (props.category !== props.activeAccordion) setActive(false)
    }
  }, [props.activeAccordion]);


  return (
    <span
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`${isActive ? "is-active" : ""}`}
      ref={ref}
    >
      <span>
        {props.iconPath &&
          <MaskImage img={props.iconPath}></MaskImage>
        }
        {props.text}
      </span>
      <i className="agi agi-f-chevron-right"></i>
    </span>
  );
};


export function ResourceCentre(props) {
  
  let match = useMatch('/resource-centre/*');
  let pathnameBase = match.pathnameBase;

  let { pathname } = useLocation();
  
  // custom hook to extract category and article from URL
  const { activeCategory, activeArticle } = useResourceCentreParams(); 

  const [activeAccordion, setActiveAccordion] = useState(activeCategory);
  const [helpItemMinimized, setHelpItemMinimized] = useState('');

  const resourceCentreRef = useRef(null);

  // get all category slug entries
  let listOfCategories = props.data.articles.map((item) => item.categorySlug);
  // filter unique category slugs
  listOfCategories = listOfCategories.filter(
    (v, i) => listOfCategories.indexOf(v) === i
  );

  // mount: 
  useEffect(() => {

    // set primary nav to static and scrollintoview
    document.querySelector('nav.primary').classList.add('primary--static');


    setTimeout(() => {
      resourceCentreRef.current?.scrollIntoView({ behavior: 'smooth', block: "nearest" });
    }, 220);


    // unmount: 
    // reset primary nav
    return () => {
      document.querySelector('nav.primary').classList.remove('primary--static');
    };
  }, []);

  return (
    <div className="help-centre" ref={resourceCentreRef}>

      <Link to="/" className="help-centre__close">
        <svg focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="icon"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.58 12 5 17.58 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path></svg>
      </Link>

      <div className="help-centre__body">
        <div className="help-centre__nav">
          <ul>
            <li>
              <NavLink to={``} className={({ isActive }) => isActive ? " is-active" : ""}>
                <span>
                  <MaskImage img={props.data.welcomeContent.iconPath}></MaskImage>
                  Resource Centre
                </span>
              </NavLink>
              {pathname === pathnameBase && <ResourceCentreSplash data={props.data} />}
            </li>
            {listOfCategories.map((categorySlug, i) => (
              <li key={i}>
                <SubNavButton
                  text={props.data.articles.find((item) => item.categorySlug === categorySlug).category}
                  iconPath={props.data.articles.find((item) => item.categorySlug === categorySlug).categoryIconPath}
                  setActiveAccordion={setActiveAccordion} activeAccordion={activeAccordion}

                  // does the category fall under the currently active category
                  isCurrent={categorySlug === activeCategory}
                  category={categorySlug}
                  isMain={true}
                />
                <div>
                  <ul>
                    <SubCategoryNav
                      data={props.data}
                      subCatItems={props.data.articles.filter((item) => item.categorySlug === categorySlug && item.subCategorySlug)}
                      activeArticle={activeArticle}
                      setActiveAccordion={setActiveAccordion}
                      activeAccordion={activeAccordion}
                      helpItemMinimized={helpItemMinimized}
                      setHelpItemMinimized={setHelpItemMinimized}
                    />
                    {props.data.articles
                      .filter((item) => item.categorySlug === categorySlug && !item.subCategorySlug)
                      .map((item, i) => (
                        <li key={i}>
                          <NavLink
                            key={item.id}
                            to={`${item.categorySlug}/${item.slug}`}
                            className={({ isActive }) => isActive ? " is-active" : ""}
                            onClick={() => setHelpItemMinimized(!helpItemMinimized)}
                          >
                            {item.title}
                          </NavLink>
                          {item.slug === activeArticle && <Article data={props.data} categoryId={item.categorySlug} articleId={item.slug} isMobile={true} helpItemMinimized={helpItemMinimized} setHelpItemMinimized={setHelpItemMinimized} />}
                        </li>
                      ))}
                  </ul>
                </div>

              </li>
            ))}
          </ul>
        </div>
        <div className="help-centre__content">
          <Routes>
            <Route path="/" element={<ResourceCentreSplash data={props.data} />} />    
            <Route 
              path="/:categoryId/*"
              element={<Category data={props.data} helpItemMinimized={helpItemMinimized} setHelpItemMinimized={setHelpItemMinimized} />}
            />
          </Routes>
        </div>
      </div>

    </div>
  );
}

function SubCategoryNav(props) {
  if (props.subCatItems.length < 1) return null;

  // get all category slug entries
  let listOfSubCategories = props.subCatItems.map((item) => item.subCategorySlug);

  // filter unique category slugs
  listOfSubCategories = listOfSubCategories.filter(
    (v, i) => listOfSubCategories.indexOf(v) === i && v
  );

  return (
    <>
      {
        listOfSubCategories.map((subCategorySlug, i) => (
          <li key={i}>
            <SubNavButton
              // find subCat name based on current slug
              text={props.subCatItems.find((item) => item.subCategorySlug === subCategorySlug).subCategory}
              parentText={props.subCatItems.find((item) => item.subCategorySlug === subCategorySlug).category}
              setActiveAccordion={props.setActiveAccordion} activeAccordion={props.activeAccordion}
              category={subCategorySlug}

              // does the sub category fall under the currently active article
              isCurrent={props.subCatItems.filter((item) => item.subCategorySlug === subCategorySlug).some((item) => item.slug === props.activeArticle)}
              isMain={false}
            />
            <div>
              <ul>
                {props.subCatItems
                  .filter((item) => item.subCategorySlug === subCategorySlug)
                  .map((item, i) => (
                    <li key={i}>
                      <NavLink
                        key={item.id}
                        to={`${item.categorySlug}/${item.slug}`}
                        className={({ isActive }) => isActive ? " is-active" : ""}
                        onClick={() => props.setHelpItemMinimized(!props.helpItemMinimized)}
                      >
                        {item.title}
                      </NavLink>
                      {item.slug === props.activeArticle && <Article data={props.data} categoryId={item.categorySlug} articleId={item.slug} isMobile={true} helpItemMinimized={props.helpItemMinimized} setHelpItemMinimized={props.setHelpItemMinimized} />}
                    </li>
                  ))}
              </ul>
            </div>
          </li>
        ))
      }
    </>
  )

}

function ResourceCentreSplash(props) {  

  let { pathname } = useLocation();

  const $rootEl = document.querySelector('#root');

  return (
    <div className="help-centre-home">
      <Helmet>
        <title>{props.data.pageTitlePrefix + props.data.welcomeContent.pageTitle}</title>

        <meta name="description" content={props.data.welcomeContent.description} />
        <link rel="canonical" href={window.location.origin + $rootEl.getAttribute('data-basename') + pathname} />
        <meta property="og:url" content={window.location.origin + $rootEl.getAttribute('data-basename') + pathname} />
        <meta property="og:title" content={props.data.pageTitlePrefix + props.data.welcomeContent.pageTitle} />
      </Helmet>

      <div className="help-centre-home__copy">
        <h2>{props.data.welcomeContent.title}</h2>
        <div
          dangerouslySetInnerHTML={{
            __html: props.data.welcomeContent.copy
          }}
        ></div>
      </div>
      <FeaturedHelpTopics data={props.data} title={props.data.welcomeContent.featuredTitle} limit={props.data.welcomeContent.numFeatured} type="resource-centre" />
      {/* <Link to="/" className="help-centre__close">
        <svg focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="icon"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.58 12 5 17.58 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path></svg>
      </Link> */}
    </div>
  );
}

function Category(props) {

  let { categoryId } = useParams();
  let location = useLocation();


  // get articles within current category
  let categoryArticles = props.data.articles.filter(
    (item) => item.categorySlug === categoryId
  );

  if (categoryArticles.length < 1) {
    return <ArticleNotFound data={props.data.articlesNotFound} />
  }

  // build default url based on first article within current category
  let categoryInitUrl = location.pathname + "/" + categoryArticles[0]?.slug;

 

  return (
    <>
      <Routes>
        <Route path="/" element={<Navigate to={`${categoryInitUrl}`} replace /> } />   
        <Route 
          path="/:articleId" 
          element={ <Article data={props.data} helpItemMinimized={props.helpItemMinimized} setHelpItemMinimized={props.setHelpItemMinimized} />} 
        />   
      </Routes>
    </>
  );
}
